import { FC, MouseEvent, useState } from "react";
import DOMPurify from "dompurify";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";

import { FaRegFileAlt } from "react-icons/fa";
import DownloadIcon from "../icons/DownloadIcon";
import { LogoIcon } from "../icons/LogoIcon";

import { apiService } from "../../services";
import { API_CONFIG } from "../../services/apiConfig";
import type { IArticle, ITicketFile } from "../../types/tickets";
import { resolveCitationHref } from "../../utils/backendUrl";
import { NEUTRAL_ICON_URL } from "../../utils/neutralBranding";
import { useOrgLinkBranding } from "../../contexts/AuthContext";

interface TicketMessageCardProps {
  article: IArticle;
  ticketId: number;
  /** Files the support form linked from this article's body. */
  linkedFiles?: ITicketFile[];
  formatDateTime: (d: string) => string;
}

type TicketFileAction = "view" | "download";

interface TicketFileEntry {
  key: string;
  filename: string;
  size: string | null;
  mimeType: string | null;
  /** Browsable URL for the link itself. */
  href: string | undefined;
  /** What apiService.downloadTicketFile fetches. */
  downloadPath: string | undefined;
}

/**
 * Fetch a ticket file from its backend URL (session cookie auth) and either save
 * it or show it in a new tab. Cross-origin `<a download>`/links can't carry the
 * session the way fetch does, so both actions go through the blob.
 */
const openTicketFile = async (
  file: TicketFileEntry,
  action: TicketFileAction,
) => {
  if (!file.href || !file.downloadPath) return;

  // Open the tab before awaiting: a window.open after the fetch is no longer a
  // direct result of the click and gets popup-blocked.
  const tab = action === "view" ? window.open("", "_blank") : null;

  const response = await apiService.downloadTicketFile(file.downloadPath);
  if (!response.success || !response.data) {
    console.error("Failed to fetch ticket file:", response.error);
    if (tab) tab.location.href = file.href;
    else window.open(file.href, "_blank", "noopener,noreferrer");
    return;
  }

  let { blob } = response.data;
  // A generic type would make the new tab download instead of display it.
  if (
    file.mimeType &&
    (!blob.type || blob.type === "application/octet-stream")
  ) {
    blob = new Blob([blob], { type: file.mimeType });
  }

  const url = URL.createObjectURL(blob);
  if (tab) {
    tab.location.href = url;
    // The tab needs the URL while it loads; free it once it surely has.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const TicketMessageCard: FC<TicketMessageCardProps> = ({
  article,
  ticketId,
  linkedFiles = [],
  formatDateTime,
}) => {
  const theme = useTheme();
  const { whitelabel } = useOrgLinkBranding();
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());

  const files: TicketFileEntry[] = [
    ...article.attachments.map((att) => ({
      key: `attachment-${att.id}`,
      filename: att.filename,
      size: att.size,
      mimeType: att.content_type || null,
      href: apiService.getTicketAttachmentUrl(ticketId, article.id, att.id),
      downloadPath: API_CONFIG.ENDPOINTS.TICKET_ATTACHMENT(
        ticketId,
        article.id,
        att.id,
      ),
    })),
    ...linkedFiles.map((file) => ({
      key: file.id,
      filename: file.filename,
      size: null,
      mimeType: file.mime_type,
      href: resolveCitationHref(file.download_url),
      downloadPath: file.download_url?.trim() || undefined,
    })),
  ];

  const handleFile = async (
    file: TicketFileEntry,
    action: TicketFileAction,
  ) => {
    if (loadingKeys.has(file.key)) return;

    setLoadingKeys((prev) => new Set(prev).add(file.key));
    try {
      await openTicketFile(file, action);
    } finally {
      setLoadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(file.key);
        return next;
      });
    }
  };

  const isAgent = article.sender?.toLowerCase() !== "customer";
  const displayName = isAgent
    ? whitelabel ? "Support" : "Reve Support"
    : article.from || article.sender || "Unknown";
  const initial = displayName.trim().charAt(0).toUpperCase() || "?";

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: "1px solid",
        borderColor: isAgent ? "primary.main": "divider",
        borderRadius: "12px",
        backgroundColor: isAgent
          ? "custom.ticketAgentCardBackground"
          : "background.paper",
      }}
    >
      <Box
        sx={{
          display: "flex",
          // Agent replies: date left, sender right; customer messages mirror it.
          flexDirection: isAgent ? "row" : "row-reverse",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", flexShrink: 0 }}
        >
          {formatDateTime(article.created_at)}
        </Typography>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
        >
          {isAgent ? (
            <Box
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& svg": { width: "100%", height: "100%" },
              }}
            >
              {whitelabel ? <img src={NEUTRAL_ICON_URL} alt="" /> : <LogoIcon />}
            </Box>
          ) : (
            <Avatar
              sx={{
                width: 28,
                height: 28,
                fontSize: "0.8rem",
                fontWeight: 700,
                flexShrink: 0,
                bgcolor: "custom.pinkLight",
                color: "custom.pinkMain",
              }}
            >
              {initial}
            </Avatar>
          )}
          <Typography noWrap sx={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
          {!isAgent ? (
            <Chip
              label="You"
              size="small"
              sx={{
                height: 20,
                flexShrink: 0,
                backgroundColor: "secondary.main",
                "& .MuiChip-label": {
                  px: 1,
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "text.secondary",
                },
              }}
            />
          ) : null}
        </Box>
      </Box>

      <Box sx={isAgent ? {} : { ml: "36px" }}>
        {article.content_type === "text/html" ? (
          <Box
            sx={{
              fontSize: "14px",
              "& img": { maxWidth: "100%" },
              "& a": { color: theme.palette.primary.main },
              wordBreak: "break-word",
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(article.body),
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: "14px",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {article.body}
          </Typography>
        )}

        {files.length > 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            {files.map((file) => (
              <Box
                key={file.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1,
                  width: "fit-content",
                  maxWidth: "100%",
                  textDecoration: "none",
                  color: "text.primary",
                  backgroundColor: "white",
                }}
              >
                <FaRegFileAlt size="14px" style={{ minWidth: "12px" }} />
                <Typography
                  noWrap
                  component="a"
                  href={file.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                    event.preventDefault();
                    handleFile(file, "view");
                  }}
                  sx={{
                    fontWeight: 500,
                    fontSize: "12px",
                    color: "inherit",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                  }}
                >
                  {file.filename}
                </Typography>
                {file.size ? (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      flexShrink: 0,
                      fontSize: "12px",
                    }}
                  >
                    {file.size}
                  </Typography>
                ) : null}
                <IconButton
                  size="small"
                  aria-label={`Download ${file.filename}`}
                  disabled={!file.downloadPath || loadingKeys.has(file.key)}
                  onClick={() => handleFile(file, "download")}
                  sx={{
                    flexShrink: 0,
                    ml: 0.5,
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": { color: "primary.main" },
                  }}
                >
                  {loadingKeys.has(file.key) ? (
                    <CircularProgress
                      size={16}
                      color="inherit"
                      aria-label={`Loading ${file.filename}`}
                    />
                  ) : (
                    <DownloadIcon size={16} />
                  )}
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default TicketMessageCard;
